/**
 * Google Identity Services token client wrapper.
 *
 * Scope is exactly drive.file: the app can only see and edit files it created
 * itself (or that the user picked in the Google Picker).
 *
 * The token model is the implicit flow: ~1 h access tokens, popup only, no
 * refresh token. Two ways to get a token:
 *
 *  - `requestAccessTokenFromGesture()` opens the Google popup. Browsers only
 *    allow the popup when it is opened synchronously inside a user gesture, so
 *    this function MUST be called directly in a click handler, before any
 *    `await` (never after a file pick or a fetch). Call `preloadGoogleIdentity()`
 *    when the screen mounts so the GIS script and token client are ready and
 *    the request really is synchronous.
 *  - `getSilentToken()` tries `prompt: 'none'` and returns null when Google
 *    needs the user; it never shows a dialog. A null result flips the module
 *    level `needsSignIn` flag which the Drive status pill reads.
 *
 * The GIS object is injectable (`configureAuth`) so the flow is unit-testable
 * without a browser.
 */
export const DRIVE_SCOPE = 'https://www.googleapis.com/auth/drive.file';
const GIS_SRC = 'https://accounts.google.com/gsi/client';
/** Tokens are treated as expired this long before Google says so. */
const EXPIRY_MARGIN_MS = 60_000;

export type AuthErrorKind =
  | 'popup_failed_to_open'
  | 'popup_closed'
  | 'access_denied'
  | 'needs_sign_in'
  | 'not_configured'
  | 'busy'
  | 'unknown';

/** Swedish, user-facing; `technical` carries Google's own wording. */
export class AuthError extends Error {
  constructor(
    public readonly kind: AuthErrorKind,
    message: string,
    public readonly technical?: string,
  ) {
    super(message);
    this.name = 'AuthError';
  }
}

const AUTH_MESSAGES: Record<AuthErrorKind, string> = {
  popup_failed_to_open: 'Google-fönstret kunde inte öppnas. Tillåt popup-fönster för den här sidan och försök igen.',
  popup_closed: 'Inloggningen avbröts. Logga in igen för att fortsätta.',
  access_denied: 'Åtkomst till Google Drive nekades. Appen behöver rätt att spara sina egna filer i din Drive.',
  needs_sign_in: 'Google-inloggning krävs. Logga in igen för att fortsätta.',
  not_configured: 'Google Drive är inte konfigurerat (VITE_GOOGLE_CLIENT_ID saknas).',
  busy: 'En inloggning pågår redan.',
  unknown: 'Inloggningen hos Google misslyckades. Försök igen.',
};

export function authError(kind: AuthErrorKind, technical?: string): AuthError {
  return new AuthError(kind, AUTH_MESSAGES[kind], technical);
}

export interface GisLike {
  initTokenClient(config: google.accounts.oauth2.TokenClientConfig): google.accounts.oauth2.TokenClient;
  revoke(accessToken: string, done?: () => void): void;
}

export interface AuthDeps {
  /** Loads (or fakes) the GIS library. */
  loadGis: () => Promise<GisLike>;
  clientId: () => string | undefined;
  now: () => number;
}

export interface AuthState {
  signedIn: boolean;
  needsSignIn: boolean;
  expiresAt: number | null;
}

interface CachedToken {
  accessToken: string;
  expiresAt: number;
}

let deps: AuthDeps = defaultDeps();
let gisPromise: Promise<GisLike> | null = null;
let tokenClient: google.accounts.oauth2.TokenClient | null = null;
let currentToken: CachedToken | null = null;
let needsSignIn = false;
let pending: { resolve: (t: string) => void; reject: (e: Error) => void; prompt: google.accounts.oauth2.Prompt } | null = null;
let inflight: Promise<string> | null = null;
const listeners = new Set<(s: AuthState) => void>();

function defaultDeps(): AuthDeps {
  return {
    loadGis: loadGisScript,
    clientId: () => import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined,
    now: () => Date.now(),
  };
}

function loadGisScript(): Promise<GisLike> {
  return new Promise<GisLike>((resolve, reject) => {
    if (typeof google !== 'undefined' && google.accounts?.oauth2) return resolve(google.accounts.oauth2);
    if (typeof document === 'undefined') return reject(authError('unknown', 'no document'));
    const script = document.createElement('script');
    script.src = GIS_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (typeof google !== 'undefined' && google.accounts?.oauth2) resolve(google.accounts.oauth2);
      else reject(authError('unknown', 'GIS loaded without accounts.oauth2'));
    };
    script.onerror = () => reject(authError('unknown', 'Kunde inte ladda Google Identity Services'));
    document.head.appendChild(script);
  });
}

/** Replace the GIS loader / clock (tests). Resets all cached state. */
export function configureAuth(partial: Partial<AuthDeps>): void {
  deps = { ...defaultDeps(), ...partial };
  resetAuth();
}

/** Forget the token, the token client and the loaded library (tests, sign-out). */
export function resetAuth(): void {
  gisPromise = null;
  tokenClient = null;
  currentToken = null;
  needsSignIn = false;
  pending = null;
  inflight = null;
}

export function getClientId(): string {
  const id = deps.clientId();
  if (!id) throw authError('not_configured');
  return id;
}

/** True when a client id is configured, i.e. Drive features can be offered at all. */
export function driveConfigured(): boolean {
  return !!deps.clientId();
}

export function authState(): AuthState {
  return { signedIn: isSignedIn(), needsSignIn, expiresAt: currentToken?.expiresAt ?? null };
}

export function onAuthStateChange(listener: (s: AuthState) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function emit(): void {
  const s = authState();
  for (const l of listeners) l(s);
}

function setNeedsSignIn(value: boolean): void {
  if (needsSignIn === value) return;
  needsSignIn = value;
  emit();
}

function mapTokenError(error: string | undefined, description: string | undefined): AuthError {
  const technical = description ? `${error}: ${description}` : error;
  switch (error) {
    case 'access_denied':
      return authError('access_denied', technical);
    case 'interaction_required':
    case 'login_required':
    case 'consent_required':
    case 'invalid_grant':
      return authError('needs_sign_in', technical);
    default:
      return authError('unknown', technical);
  }
}

function mapErrorCallback(err: { type: string; message?: string }): AuthError {
  switch (err.type) {
    case 'popup_failed_to_open':
      return authError('popup_failed_to_open', err.message);
    case 'popup_closed':
      return authError('popup_closed', err.message);
    default:
      return authError('unknown', err.message ?? err.type);
  }
}

function settlePending(outcome: { token: string } | { error: AuthError }): void {
  const p = pending;
  pending = null;
  inflight = null;
  if (!p) return;
  if ('token' in outcome) {
    needsSignIn = false;
    p.resolve(outcome.token);
  } else {
    // A silent failure means Google wants to see the user; an interactive one
    // may be a closed popup, which is not the same as "signed out".
    if (p.prompt === 'none' || outcome.error.kind === 'access_denied') needsSignIn = true;
    p.reject(outcome.error);
  }
  emit();
}

async function ensureTokenClient(): Promise<google.accounts.oauth2.TokenClient> {
  if (tokenClient) return tokenClient;
  const clientId = getClientId();
  gisPromise ??= deps.loadGis();
  const gis = await gisPromise;
  if (tokenClient) return tokenClient;
  tokenClient = gis.initTokenClient({
    client_id: clientId,
    scope: DRIVE_SCOPE,
    callback: (response) => {
      if (response.error || !response.access_token) {
        settlePending({ error: mapTokenError(response.error, response.error_description) });
        return;
      }
      if (!response.scope.split(' ').includes(DRIVE_SCOPE)) {
        settlePending({ error: authError('access_denied', `scope granted: ${response.scope}`) });
        return;
      }
      currentToken = {
        accessToken: response.access_token,
        expiresAt: deps.now() + Math.max(0, response.expires_in * 1000 - EXPIRY_MARGIN_MS),
      };
      settlePending({ token: response.access_token });
    },
    error_callback: (err) => settlePending({ error: mapErrorCallback(err) }),
  });
  return tokenClient;
}

/**
 * Load the GIS script and initialise the token client ahead of time so that
 * `requestAccessTokenFromGesture` can call Google synchronously in the click.
 * Safe to call repeatedly; failures are swallowed (the gesture path retries).
 */
export async function preloadGoogleIdentity(): Promise<void> {
  try {
    await ensureTokenClient();
  } catch {
    // Reported when the user actually tries to sign in.
  }
}

function startRequest(client: google.accounts.oauth2.TokenClient, prompt: google.accounts.oauth2.Prompt): Promise<string> {
  const promise = new Promise<string>((resolve, reject) => {
    pending = { resolve, reject, prompt };
    try {
      client.requestAccessToken({ prompt });
    } catch (e) {
      pending = null;
      reject(authError('unknown', e instanceof Error ? e.message : String(e)));
    }
  });
  inflight = promise;
  return promise;
}

/**
 * Open the Google sign-in popup and resolve with an access token.
 *
 * MUST be invoked synchronously inside a user gesture (a click handler),
 * before any `await`. Popup blockers reject popups opened later; the error
 * then surfaces as `popup_failed_to_open` ("Logga in igen" in the UI).
 * Call `preloadGoogleIdentity()` on mount so the client is already there.
 */
export function requestAccessTokenFromGesture(opts: { prompt?: '' | 'consent' | 'select_account' } = {}): Promise<string> {
  const prompt = opts.prompt ?? '';
  if (inflight) return Promise.reject(authError('busy'));
  if (tokenClient) return startRequest(tokenClient, prompt);
  // Not preloaded: the popup may be blocked once the script has loaded. We
  // still try, because on many browsers the gesture survives a short await.
  const promise = ensureTokenClient().then((client) => {
    if (inflight && inflight !== promise) throw authError('busy');
    return startRequest(client, prompt);
  });
  inflight = promise;
  return promise.finally(() => {
    if (inflight === promise) inflight = null;
  });
}

/**
 * A valid token without any dialog: the cached one if not expired, otherwise
 * a `prompt: 'none'` refresh. Returns null (and sets `needsSignIn`) when
 * Google needs the user, the popup was blocked, or Drive is not configured.
 */
export async function getSilentToken(): Promise<string | null> {
  if (currentToken && currentToken.expiresAt > deps.now()) return currentToken.accessToken;
  if (!driveConfigured()) return null;
  try {
    if (inflight) return await inflight;
    const client = await ensureTokenClient();
    if (inflight) return await inflight;
    return await startRequest(client, 'none');
  } catch {
    setNeedsSignIn(true);
    return null;
  }
}

/**
 * Backwards-compatible helper: interactive requests go through the gesture
 * path, non-interactive ones through the silent path (throws when it fails).
 */
export async function getAccessToken(opts: { interactive?: boolean } = {}): Promise<string> {
  if (opts.interactive === false) {
    const token = await getSilentToken();
    if (!token) throw authError('needs_sign_in');
    return token;
  }
  if (currentToken && currentToken.expiresAt > deps.now()) return currentToken.accessToken;
  return requestAccessTokenFromGesture();
}

export function isSignedIn(): boolean {
  return !!currentToken && currentToken.expiresAt > deps.now();
}

/** Drop the cached token (after a 401) so the next call refreshes silently. */
export function invalidateToken(): void {
  currentToken = null;
  emit();
}

/** Forget the token locally and revoke it at Google. Never throws. */
export async function signOut(): Promise<void> {
  const token = currentToken?.accessToken;
  currentToken = null;
  needsSignIn = false;
  emit();
  if (!token || !gisPromise) return;
  try {
    const gis = await gisPromise;
    await new Promise<void>((resolve) => gis.revoke(token, resolve));
  } catch {
    // Revocation is best effort; the token expires within the hour anyway.
  }
}
